/**
 * Autosave and refresh recovery.
 *
 * The reducer is pure and the store is in memory, so a refresh — or iOS quietly evicting
 * the tab while the player is answering the door — would lose the session. This file is
 * what makes that survivable: every state change schedules a debounced write of the whole
 * `SessionState`, and a cold start can offer it back.
 *
 * ## Why debounced, and why 500 ms
 *
 * `revision` changes on every action, and a sustained chord alone produces one every few
 * hundred milliseconds. Writing each of those would mean a structured clone of the entire
 * session per event, on the thread that is also running the recogniser. 500 ms is long
 * enough to collapse a burst of sustains into one write and short enough that the worst
 * case loss is a single chord.
 *
 * ## Why restoring is not just `dispatch({ type: 'RESTORED' })`
 *
 * The action's contract says to validate before dispatching, and there are two distinct
 * reasons to. A snapshot may be *malformed* — written by an older version, or truncated —
 * and putting that into the reducer would corrupt the graph. And a snapshot is always
 * *stale in specific ways*: it may claim the microphone is granted when this page load has
 * never asked for it, or claim a branch set is still loading when the request that would
 * have filled it died with the previous page. `sanitizeRestoredState` fixes both.
 */

import {
  DEFAULT_KEY_ESTIMATE,
  retainedNodeIds,
  type BranchSet,
  type SessionNode,
  type SessionState,
} from '@hc/shared';
import {
  appendCorrection,
  deleteSessionSnapshot,
  latestSessionSnapshot,
  loadSessionSnapshot,
  pruneSessionSnapshots,
  saveSessionSnapshot,
  type CorrectionLogEntry,
} from '../sketchbook/queries.js';
import { storageStatus } from '../sketchbook/db.js';

/** Long enough to collapse a burst of sustains, short enough to lose at most one chord. */
export const SNAPSHOT_DEBOUNCE_MS = 500;

export interface SessionPersistence {
  /** Debounced. Safe to call on every state change; that is what it is for. */
  scheduleSave(state: SessionState): void;
  /** Write now, ignoring the debounce. Used on page hide and before navigation. */
  save(state: SessionState): Promise<void>;
  /** Resolve once any pending debounced write has completed. */
  flush(): Promise<void>;
  load(sessionId: string): Promise<SessionState | null>;
  /** The session to offer resuming on a cold start. */
  loadLatest(): Promise<SessionState | null>;
  forget(sessionId: string): Promise<void>;
  logCorrection(entry: CorrectionLogEntry): Promise<void>;
  /** False in private browsing: writes happen but do not outlive the tab. */
  durable(): boolean;
  /** Cancel the pending write and stop. */
  dispose(): void;
}

export interface CreateSessionPersistenceOptions {
  debounceMs?: number;
  /** Injected in tests. Defaults to the real IndexedDB-backed queries. */
  writer?: {
    saveSnapshot: (state: SessionState, savedAt: number) => Promise<void>;
    loadSnapshot: (sessionId: string) => Promise<SessionState | null>;
    loadLatestSnapshot: () => Promise<SessionState | null>;
    deleteSnapshot: (sessionId: string) => Promise<void>;
    appendCorrection: (entry: CorrectionLogEntry) => Promise<void>;
  };
  now?: () => number;
  onError?: (error: unknown) => void;
}

const defaultWriter: NonNullable<CreateSessionPersistenceOptions['writer']> = {
  saveSnapshot: async (state, savedAt) => {
    await saveSessionSnapshot(state, savedAt);
    await pruneSessionSnapshots();
  },
  loadSnapshot: async (sessionId) => {
    const record = await loadSessionSnapshot(sessionId);
    return record ? sanitizeRestoredState(record.state) : null;
  },
  loadLatestSnapshot: async () => {
    const record = await latestSessionSnapshot();
    return record ? sanitizeRestoredState(record.state) : null;
  },
  deleteSnapshot: deleteSessionSnapshot,
  appendCorrection,
};

export function createSessionPersistence(
  options: CreateSessionPersistenceOptions = {},
): SessionPersistence {
  const debounceMs = options.debounceMs ?? SNAPSHOT_DEBOUNCE_MS;
  const writer = options.writer ?? defaultWriter;
  const now = options.now ?? (() => Date.now());
  const onError = options.onError ?? (() => {});

  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: SessionState | null = null;
  let inFlight: Promise<void> = Promise.resolve();
  let lastSavedRevision = -1;
  let disposed = false;

  async function write(state: SessionState): Promise<void> {
    try {
      await writer.saveSnapshot(state, now());
      lastSavedRevision = state.revision;
    } catch (error) {
      // A failed write must never take the session down with it. The storage layer has
      // already degraded to memory by this point; the UI is showing the chip.
      onError(error);
    }
  }

  function fire(): void {
    timer = null;
    const state = pending;
    pending = null;
    if (!state) return;
    inFlight = inFlight.then(() => write(state));
  }

  return {
    scheduleSave(state) {
      if (disposed) return;
      // Nothing changed that anyone would miss.
      if (state.revision === lastSavedRevision) return;
      pending = state;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, debounceMs);
    },
    async save(state) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pending = null;
      inFlight = inFlight.then(() => write(state));
      await inFlight;
    },
    async flush() {
      if (timer !== null) {
        clearTimeout(timer);
        fire();
      }
      await inFlight;
    },
    load: (sessionId) => writer.loadSnapshot(sessionId),
    loadLatest: () => writer.loadLatestSnapshot(),
    forget: (sessionId) => writer.deleteSnapshot(sessionId),
    logCorrection: (entry) => writer.appendCorrection(entry),
    durable: () => storageStatus().durable,
    dispose() {
      disposed = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}

// ---------------------------------------------------------------------------
// Validation and normalisation
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNode(value: unknown): value is SessionNode {
  if (!isRecord(value)) return false;
  if (typeof value.id !== 'string' || typeof value.playedAt !== 'number') return false;
  const chord = value.chord;
  return isRecord(chord) && Array.isArray(chord.pitchClasses) && typeof chord.symbol === 'string';
}

/**
 * Take whatever came out of storage and return something the reducer can safely be handed,
 * or `null`.
 *
 * The normalisations are as important as the validation:
 *
 *  - `listening` and `micStatus` are reset. A restored session has no `MediaStream`; a
 *    state that says "active, granted" would make the UI show a listening pulse over a
 *    microphone nobody has asked for yet.
 *  - A branch set left `loading` is marked `failed`: the request that would have completed
 *    it belongs to a page that no longer exists, so the UI should offer a retry rather than
 *    a spinner that never stops. A `partial` set becomes `ready` — what arrived is real.
 *  - Branch sets are re-pruned with the reducer's own rule, in case the snapshot predates it.
 */
export function sanitizeRestoredState(value: unknown): SessionState | null {
  if (!isRecord(value)) return null;
  if (typeof value.sessionId !== 'string' || value.sessionId === '') return null;
  if (!Array.isArray(value.path)) return null;
  if (!isRecord(value.keyEstimate)) return null;

  const path = value.path.filter(isNode);
  // A path that lost nodes in validation is a corrupt snapshot, not a shorter session.
  if (path.length !== value.path.length) return null;

  const currentNodeId =
    typeof value.currentNodeId === 'string' && path.some((node) => node.id === value.currentNodeId)
      ? value.currentNodeId
      : (path[path.length - 1]?.id ?? null);

  const branchSets: Record<string, BranchSet> = {};
  const keep = new Set(retainedNodeIds(path, currentNodeId));
  const storedSets = isRecord(value.branchSets) ? value.branchSets : {};
  for (const [nodeId, set] of Object.entries(storedSets)) {
    if (!keep.has(nodeId) || !isRecord(set)) continue;
    const suggestions = Array.isArray(set.suggestions) ? set.suggestions : [];
    const status = set.status;
    branchSets[nodeId] = {
      forNodeId: nodeId,
      suggestions: suggestions as BranchSet['suggestions'],
      status:
        status === 'ready' || status === 'fallback' || status === 'failed'
          ? status
          : status === 'partial'
            ? 'ready'
            : 'failed',
      requestedAt: typeof set.requestedAt === 'number' ? set.requestedAt : 0,
    };
  }

  const storedKey = value.keyEstimate;
  const keyEstimate: SessionState['keyEstimate'] =
    typeof storedKey.tonicPc === 'number' &&
    storedKey.tonicPc >= 0 &&
    storedKey.tonicPc <= 11 &&
    (storedKey.mode === 'major' || storedKey.mode === 'minor')
      ? (storedKey as unknown as SessionState['keyEstimate'])
      : { ...DEFAULT_KEY_ESTIMATE };

  return {
    sessionId: value.sessionId,
    startedAt: typeof value.startedAt === 'number' ? value.startedAt : Date.now(),
    revision: typeof value.revision === 'number' ? value.revision : 0,
    ...(typeof value.intention === 'string' ? { intention: value.intention } : {}),
    perspectives: Array.isArray(value.perspectives)
      ? (value.perspectives as SessionState['perspectives'])
      : [],
    adventurousness: typeof value.adventurousness === 'number' ? value.adventurousness : 0.5,
    path,
    currentNodeId,
    branchSets,
    keyEstimate,
    llmHealthy: value.llmHealthy !== false,
    // Runtime-only facts that a restored snapshot cannot vouch for.
    listening: 'idle',
    micStatus: 'unrequested',
  };
}
